import { cn } from '@syncanix/ui';

import { searchOptionId } from './search-option-id';

import type { ReactElement, ReactNode } from 'react';

/**
 * The listbox both search surfaces render.
 *
 * The rows started in `CommandPalette.tsx` and are here because the top-bar
 * field shows the same thing: a name, where it lives, and a highlight that moves
 * with the arrow keys. Two copies of that markup would be two answers to how a
 * result LOOKS and, more to the point, two chances for one of them to lose the
 * `aria-selected` wiring that makes the whole surface usable without a mouse.
 *
 * Presentational only. It owns no query, no filtering and no navigation — the
 * caller decides which rows exist, which is highlighted, and what a row does.
 */

export interface SearchResultRow {
  /** Stable id — the React key, and the basis of each option's DOM id. */
  readonly key: string;
  /** Localized name, as the thing it opens renders it. */
  readonly label: string;
  /**
   * Localized "Page › Tab › Card" trail. Rendered as one muted line at the end
   * of the row, so the name leads and the location answers "which one?" without
   * competing with it.
   */
  readonly trail?: readonly string[] | undefined;
  readonly icon?: ReactNode;
  readonly onSelect: () => void;
}

export interface SearchResultsListProps {
  /** DOM id, so the input can point at it with `aria-controls`. */
  readonly id: string;
  readonly ariaLabel: string;
  readonly rows: readonly SearchResultRow[];
  /** Index of the highlighted row; ignored when there are none. */
  readonly highlight: number;
  /** Called as the pointer moves, so hover and keyboard share one highlight. */
  readonly onHighlight: (index: number) => void;
  /** Shown in place of the rows when nothing matched. */
  readonly emptyLabel: string;
  /** Extra classes for the list — usually the max height and its scroll. */
  readonly className?: string;
}

export function SearchResultsList({
  id,
  ariaLabel,
  rows,
  highlight,
  onHighlight,
  emptyLabel,
  className,
}: SearchResultsListProps): ReactElement {
  return (
    <ul id={id} role="listbox" aria-label={ariaLabel} className={cn('p-1.5', className)}>
      {rows.length === 0 ? (
        <li className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyLabel}</li>
      ) : (
        rows.map((row, index) => (
          // The OPTION is the clickable element, with no button inside it. A
          // `<button role="presentation">` wrapper is what a list like this
          // usually grows, and axe calls it `nested-interactive`: a control
          // inside an option is a second thing to operate where the pattern
          // promises one. It also contradicts what the input announces — in a
          // combobox with `aria-activedescendant` the focus never leaves the
          // input, so an option must not be a focus target at all.
          //
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- the keyboard path is the combobox input (ArrowUp/Down + Enter); an option here is never focused
          <li
            key={row.key}
            id={searchOptionId(id, row.key)}
            role="option"
            aria-selected={index === highlight}
            onClick={row.onSelect}
            onMouseMove={() => {
              onHighlight(index);
            }}
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-start text-sm text-foreground',
              index === highlight ? 'bg-muted' : 'hover:bg-muted/60',
            )}
          >
            {row.icon === undefined ? null : (
              <span className="shrink-0 text-muted-foreground">{row.icon}</span>
            )}
            <span className="min-w-0 flex-1 truncate">{row.label}</span>
            {row.trail === undefined || row.trail.length === 0 ? null : (
              // The NAME is the part that must survive, so the trail is CAPPED
              // rather than merely shrinkable. Measured in German at 1024px:
              // with both sides free the trail keeps its full content width and
              // the name truncates instead — "Aufbewahrung von Unterhalt…"
              // beside a complete "Arbeitsbereich › Daten & Datenschutz", which
              // is the wrong half to lose.
              <span className="min-w-0 max-w-[45%] shrink truncate text-xs text-muted-foreground">
                {row.trail.map((part, partIndex) => (
                  <span key={part}>
                    {partIndex === 0 ? null : (
                      // Punctuation, not copy — and `aria-hidden` because a
                      // screen reader reading "Workspace right angle quote
                      // Data" is worse than reading the two names.
                      <span aria-hidden="true"> › </span>
                    )}
                    {part}
                  </span>
                ))}
              </span>
            )}
          </li>
        ))
      )}
    </ul>
  );
}
